import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { formatDatum } from '@/lib/format'
import { listMerges } from './api'
import type { MergeItem, MergeKind, UndoResult } from './types'
import { useUndoMerge } from './useUndoMerge'

const PATHS: Record<MergeKind, string> = { organisatie: '/relaties/organisaties', contact: '/relaties/contacten' }

/**
 * "Samengevoegd met <naam> op <datum> door <wie> · Ongedaan maken", at the top of the main column
 * of a relation page, only while the newest merge can still be undone (spec-68). After an undo
 * the page reloads (`onUndone`) and the notice becomes a link to the restored relation.
 */
export function MergeNotice({
  kind,
  relationId,
  survivorName,
  onUndone,
}: {
  kind: MergeKind
  relationId: string
  survivorName: string
  onUndone: () => void
}) {
  const [item, setItem] = useState<MergeItem | null>(null)
  const [restored, setRestored] = useState<{ id: string; name: string } | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [dialog, undo] = useUndoMerge()

  useEffect(() => {
    let cancelled = false
    listMerges(kind, relationId)
      .then((items) => {
        if (!cancelled) setItem(items.find((candidate) => candidate.can_undo) ?? null)
      })
      .catch(() => {
        // The notice is an extra: without an answer there is simply none.
        if (!cancelled) setItem(null)
      })
    return () => {
      cancelled = true
    }
  }, [kind, relationId, reloadKey])

  const run = useCallback(
    async (current: MergeItem) => {
      const result: UndoResult | undefined = await undo({
        mergeId: current.id,
        survivorName,
        mergedName: current.merged_name,
        kind,
      })
      if (!result) return
      setRestored({ id: result.restored_id, name: current.merged_name })
      setItem(null)
      setReloadKey((key) => key + 1)
      onUndone()
    },
    [kind, onUndone, survivorName, undo],
  )

  if (!item && !restored) return dialog
  return (
    <>
      <div
        role="status"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-muted px-3 py-2 text-sm"
      >
        {!restored && item ? (
          <>
            <span className="min-w-0 flex-1 break-words">
              Samengevoegd met {item.merged_name} op {formatDatum(item.created_at)}
              {item.created_by_name ? ` door ${item.created_by_name}` : ''}
            </span>
            <Button type="button" variant="outline" size="sm" className="max-md:h-11" onClick={() => void run(item)}>
              Ongedaan maken
            </Button>
          </>
        ) : restored ? (
          <>
            <span className="min-w-0 flex-1 break-words">Samenvoeging ongedaan gemaakt.</span>
            <Link to={`${PATHS[kind]}/${restored.id}`} className="font-medium underline underline-offset-2">
              Naar {restored.name}
            </Link>
          </>
        ) : null}
      </div>
      {dialog}
    </>
  )
}
